import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { CustomSDSDetails } from '../../models/sds-details';
import { downloadIcon, SVGIcon } from '@progress/kendo-svg-icons';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	UntypedFormControl,
	UntypedFormGroup,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { GridLayoutGapSettings } from '@progress/kendo-angular-layout';
import { FileManagementService } from '@app/shared/services/file-management.service';
import { CertificateService } from '@app/dashboard/shared/services/certificate.service';
import { NotificationService } from '@progress/kendo-angular-notification';
import { SDSGHSLabel, SDSNonGHSLabel } from '../../models/sds-label';
import { StringConstants } from '@app/shared/string-constants';

@Component({
	selector: 'kpa-view-sds',
	templateUrl: './view-sds.component.html',
	styleUrls: ['./view-sds.component.scss'],
})
export class ViewSdsComponent implements OnInit {
	@Input() dialogOpened: boolean = false;
	@Output() closeDialog = new EventEmitter<void>();
	@Input() sdsDetails: CustomSDSDetails;
	@Input() formatTypes: Array<{ text: string; value: string }>;
	public downloadIcon: SVGIcon = downloadIcon;
	labelIndex: number = -1;
	public defaultItem: { text: string; value: string } = {
		text: '--Select Format--',
		value: null,
	};

	ghsOptions = [
		{ label: 'Pictograms', value: 'Pictograms' },
		{ label: 'Hazard Statement', value: 'HazardStatement' },
		{ label: 'Precautionary Statement', value: 'PrecautionaryStatement' },
		{ label: 'Manufacturer Information', value: 'ManufacturerInformation' },
	];

	ghsAveryTypes = [{ value: '5165' }, { value: '00761' }, { value: '5168' }, { value: 'OL178' }].map((item) => ({
		text: item.value,
		value: item.value,
	}));

	averyTypes = [
		{ value: '5160' },
		{ value: '5161' },
		{ value: '5162' },
		{ value: '5163' },
		{ value: '5164' },
		{ value: '5165' },
		{ value: '5168' },
		{ value: '5395' },
	].map((item) => ({ text: item.value, value: item.value }));

	sheets = [
		[3, 10],
		[2, 10],
		[2, 7],
		[2, 5],
		[2, 3],
		[1, 1],
		[2, 2],
		[2, 4],
		[2, 1],
		[1, 2],
	];
	labelForm: FormGroup;
	isGHS: boolean = false;
	isNonGHS: boolean = false;
	selectedCellIndices: number[] = [];
	rows: number[] = [];
	cols: number[] = [];
	items = Array.from({ length: 0 });
	showLabelLayout: boolean = false;
	constructor(
		private fb: FormBuilder,
		private fileManagementService: FileManagementService,
		private certificateService: CertificateService,
		private notificationService: NotificationService
	) {}

	ngOnInit(): void {
		this.initializeForm();
	}
	gridSelectionValidator(control: AbstractControl): ValidationErrors | null {
		return this.selectedCellIndices.length > 0 ? null : { noBoxSelected: true };
	}
	generateBinaryPattern(): string {
		const totalCells = this.rows.length * this.cols.length;
		let binaryPattern = '';

		for (let i = 0; i < totalCells; i++) {
			if (this.selectedCellIndices.includes(i)) {
				binaryPattern += '1';
			} else {
				binaryPattern += '0';
			}
		}

		return binaryPattern;
	}
	initializeForm(): void {
		this.labelForm = this.fb.group({
			formatType: [''],
			cblGhsOptions_0: [true],
			cblGhsOptions_1: [true],
			cblGhsOptions_2: [true],
			cblGhsOptions_3: [true],
			ghsAveryType: [''],
			averyType: [''],
			averyTypePromt: [true],
			gridSelection: [''],
		});
	}
	onTabChange(event: any): void {
		this.resetGrid();
		this.isGHS = false;
		this.isNonGHS = false;
		this.labelForm.reset();
		this.initializeForm();

		if (event.index === 1) {
			this.handleGhsValidators(null);
		}
	}
	handleGhsValidators(value: string): void {
		if (value) {
			const ghsControls = ['ghsAveryType'];
			const nonghsControls = ['averyType', 'averyTypePromt'];
			if (value === 'GHS') {
				this.isGHS = true;
				this.isNonGHS = false;
				ghsControls.forEach((control) => {
					this.labelForm.get(control).setValidators(Validators.required);
					this.labelForm.get(control).updateValueAndValidity();
				});
				nonghsControls.forEach((control) => {
					this.labelForm.get(control).clearValidators();
					const controlValue = this.labelForm.get(control).value;
					if (typeof controlValue === 'boolean') {
						this.labelForm.get(control).reset(true);
					} else {
						this.labelForm.get(control).reset('');
					}
					this.labelForm.get(control).updateValueAndValidity();
				});
			} else {
				this.isNonGHS = true;
				this.isGHS = false;
				ghsControls.forEach((control) => {
					this.labelForm.get(control).clearValidators();
					this.labelForm.get(control).reset('');
					this.labelForm.get(control).updateValueAndValidity();
				});
				nonghsControls.forEach((control) => {
					this.labelForm.get(control).setValidators(Validators.required);
					this.labelForm.get(control).updateValueAndValidity();
				});
			}
		}
	}
	valueChange(event: any) {
		this.resetGrid();
		if (event) {
			this.handleGhsValidators(event);
		} else {
			this.isGHS = false;
			this.isNonGHS = false;
		}
	}
	ghsAveryTypeChange(event: any) {
		if (event) {
			switch (event) {
				case '5165':
					this.labelIndex = 5;
					break;
				case '00761':
					this.labelIndex = 9;
					break;
				case '5168':
					this.labelIndex = 6;
					break;
				case 'OL178':
					this.labelIndex = 8;
					break;
			}
			this.updateGrid();
		} else {
			this.resetGrid();
		}
	}
	averyTypeChange(event: any) {
		if (event) {
			switch (event) {
				case '5160':
					this.labelIndex = 0;
					break;
				case '5161':
					this.labelIndex = 1;
					break;
				case '5162':
					this.labelIndex = 2;
					break;
				case '5163':
					this.labelIndex = 3;
					break;
				case '5164':
					this.labelIndex = 4;
					break;
				case '5165':
					this.labelIndex = 5;
					break;
				case '5168':
					this.labelIndex = 6;
					break;
				case '5395':
					this.labelIndex = 7;
					break;
			}
			this.updateGrid();
		} else {
			this.resetGrid();
		}
	}
	resetGrid() {
		this.rows = [];
		this.cols = [];
		this.items = [];
		this.showLabelLayout = false;
		this.selectedCellIndices = [];
		this.labelForm.get('gridSelection').clearValidators();
		this.labelForm.get('gridSelection').updateValueAndValidity();
	}
	updateGrid() {
		if (this.labelIndex > -1) {
			let colCount = this.sheets[this.labelIndex][0];
			let rowCount = this.sheets[this.labelIndex][1];
			let cellWidth = 150 / colCount;
			let cellHeight = 200 / rowCount;
			this.cols = Array(colCount).fill(cellWidth);
			this.rows = Array(rowCount).fill(cellHeight);
			this.items = Array.from({ length: rowCount * colCount });
			this.showLabelLayout = true;
			this.selectedCellIndices = [];
			this.labelForm.get('gridSelection').setValidators(this.gridSelectionValidator.bind(this));
			this.labelForm.get('gridSelection').updateValueAndValidity();
		} else {
			this.showLabelLayout = false;
			this.labelForm.get('gridSelection').clearValidators();
			this.labelForm.get('gridSelection').updateValueAndValidity();
		}
	}
	onCellClick(index: number) {
		const selectedIndex = this.selectedCellIndices.indexOf(index);
		if (selectedIndex > -1) {
			this.selectedCellIndices.splice(selectedIndex, 1);
		} else {
			this.selectedCellIndices.push(index);
		}
		this.labelForm.get('gridSelection').updateValueAndValidity();
	}
	public close(component: string): void {
		this[component + 'Opened'] = false;
		this.labelForm.reset();
		this.isGHS = false;
		this.isNonGHS = false;
		this.resetGrid();
		this.closeDialog.emit();
	}
	downloadSDS(fileId: number) {
		if (fileId) {
			this.fileManagementService.downloadFileByFileId(fileId).subscribe({
				next: (response) => {
					this.certificateService.downloadCert('Safety Data Sheets', response);
				},
				error: (err) => {
					this.notificationService.show({
						content: 'Error while downloading file.',
						hideAfter: StringConstants.hideNotificationAfter,
						position: { horizontal: 'right', vertical: 'bottom' },
						animation: { type: 'fade', duration: 400 },
						type: { style: 'error', icon: true },
					});
				},
			});
		}
	}
	getIsRequiredLabel(value: string) {
		const control = this.labelForm.get(value);
		const errors = control ? control.errors : null;

		return errors && control.dirty;
	}
	concatenateSelectedCheckboxValues(): string {
		const selectedValues: string[] = [];
		this.ghsOptions.forEach((option, index) => {
			const control = this.labelForm.get(`cblGhsOptions_${index}`);
			if (control && control.value) {
				selectedValues.push(option.value);
			}
		});
		return selectedValues.join('|');
	}
	downloadLabel() {
		this.showValidationMsg(this.labelForm);
		if (this.labelForm.valid) {
			const binaryPattern = this.generateBinaryPattern();

			if (this.labelForm.value.formatType === 'GHS') {
				const concatenatedValues = this.concatenateSelectedCheckboxValues();
				let sdsLabel: SDSGHSLabel = {
					format: this.labelForm.value.formatType,
					cells: binaryPattern,
					labelSize: this.labelForm.value.ghsAveryType,
					sdsId: this.sdsDetails.id,
					sectionInclude: concatenatedValues,
				};
				this.fileManagementService.downloadFileByGHSLabel(sdsLabel).subscribe({
					next: (response) => {
						this.certificateService.downloadCert('Safety Label', response);
					},
					error: (err) => {
						this.notificationService.show({
							content: 'Error while downloading file.',
							hideAfter: StringConstants.hideNotificationAfter,
							position: { horizontal: 'right', vertical: 'bottom' },
							animation: { type: 'fade', duration: 400 },
							type: { style: 'error', icon: true },
						});
					},
				});
			} else {
				let sdsLabel: SDSNonGHSLabel = {
					format: this.labelForm.value.formatType,
					cells: binaryPattern,
					labelSize: this.labelForm.value.averyType,
					sdsId: this.sdsDetails.id,
					includeNames: this.labelForm.value.averyTypePromt,
				};
				this.fileManagementService.downloadFileByNonGHSLabel(sdsLabel).subscribe({
					next: (response) => {
						this.certificateService.downloadCert('Safety Label', response);
					},
					error: (err) => {
						this.notificationService.show({
							content: 'Error while downloading file.',
							hideAfter: StringConstants.hideNotificationAfter,
							position: { horizontal: 'right', vertical: 'bottom' },
							animation: { type: 'fade', duration: 400 },
							type: { style: 'error', icon: true },
						});
					},
				});
			}
		}
	}
	showValidationMsg(formGroup: UntypedFormGroup) {
		for (const key in formGroup.controls) {
			if (formGroup.controls.hasOwnProperty(key)) {
				const control: UntypedFormControl = <UntypedFormControl>formGroup.controls[key];
				if (control) {
					control.markAsTouched();
					control.markAsDirty();
				}
			}
		}
	}
}
